import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Eye, EyeOff, Mail, Lock, ArrowRight, ArrowLeft,
  Sun, Moon, Globe, Loader2, AlertCircle
} from 'lucide-react';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';

const translations = {
  ar: {
    title: 'مرحباً بعودتك',
    subtitle: 'سجل دخولك للوصول إلى لوحة التحكم',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'admin@allista.com',
    password: 'كلمة المرور',
    passwordPlaceholder: '••••••••',
    remember: 'تذكرني',
    forgot: 'نسيت كلمة المرور؟',
    login: 'تسجيل الدخول',
    noAccount: 'ليس لديك حساب؟',
    register: 'إنشاء حساب',
    or: 'أو',
    demoLogin: 'دخول تجريبي',
    error: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    backToHome: 'العودة للرئيسية',
  },
  en: {
    title: 'Welcome Back',
    subtitle: 'Sign in to access your dashboard',
    email: 'Email',
    emailPlaceholder: 'admin@allista.com',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    remember: 'Remember me',
    forgot: 'Forgot password?',
    login: 'Sign In',
    noAccount: "Don't have an account?",
    register: 'Create Account',
    or: 'or',
    demoLogin: 'Demo Login',
    error: 'Invalid email or password',
    backToHome: 'Back to Home',
  },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme, language, toggleLanguage } = useSettingsStore();
  const { login } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const t = translations[language];
  const isRtl = language === 'ar';
  const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Demo login
      if (email === 'admin@allista.com' && password === 'admin123') {
        login('demo-token', { id: 1, name: 'المدير', email: 'admin@allista.com', role: 'admin' });
        navigate('/dashboard');
      } else {
        setError(t.error);
      }
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setLoading(true);
    setTimeout(() => {
      login('demo-token', { id: 1, name: 'المدير', email: 'admin@allista.com', role: 'admin' });
      navigate('/dashboard');
    }, 500);
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen flex">
        {/* Left Side - Form */}
        <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-slate-900">
          {/* Header */}
          <div className="flex items-center justify-between p-6">
            <Link to="/" className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
              <ArrowIcon size={20} className={isRtl ? '' : 'rotate-180'} />
              <span className="font-medium">{t.backToHome}</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLanguage}
                className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <Globe size={20} />
              </button>
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              </button>
            </div>
          </div>
          
          {/* Form Container */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md">
              {/* Logo */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-emerald-500 rounded-2xl text-3xl mb-4 shadow-lg shadow-primary-500/25">
                  🚀
                </div>
                <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                  {t.title}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {t.subtitle}
                </p>
              </div>
              
              {/* Error Alert */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl mb-6 animate-shake">
                  <AlertCircle className="text-red-500 shrink-0" size={20} />
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
                </div>
              )}
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t.email}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
                      <Mail className="text-slate-400" size={20} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.emailPlaceholder}
                      className="w-full ps-12 pe-4 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none"
                      required
                    />
                  </div>
                </div>
                
                {/* Password */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t.password}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
                      <Lock className="text-slate-400" size={20} />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t.passwordPlaceholder}
                      className="w-full ps-12 pe-12 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 end-0 pe-4 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t.remember}</span>
                  </label>
                  <a href="#" className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    {t.forgot}
                  </a>
                </div>
                
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      {t.login}
                      <ArrowIcon size={20} />
                    </>
                  )}
                </button>
              </form>
              
              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <span className="text-sm text-slate-500 dark:text-slate-400">{t.or}</span>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              
              {/* Demo Login */}
              <button
                type="button"
                onClick={handleDemoLogin}
                disabled={loading}
                className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all flex items-center justify-center gap-2"
              >
                🎯 {t.demoLogin}
              </button>
              
              {/* Register Link */}
              <p className="text-center mt-8 text-slate-600 dark:text-slate-400">
                {t.noAccount}{' '}
                <a href="#" className="text-primary-600 dark:text-primary-400 font-semibold hover:underline">
                  {t.register}
                </a>
              </p>
            </div>
          </div>
        </div>
        
        {/* Right Side - Decorative */}
        <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-primary-500 via-primary-600 to-emerald-600 p-12 items-center justify-center relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-20 w-80 h-80 bg-white rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          
          {/* Content */}
          <div className="relative text-center text-white">
            <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/20">
              <span className="text-6xl">☕</span>
            </div>
            <h2 className="text-3xl font-bold mb-4">أليستا</h2>
            <p className="text-lg text-white/80 max-w-sm">
              منصة توصيل المقاهي الأولى في مصر. إدارة طلباتك وعملائك وسائقيك من مكان واحد.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-2xl font-bold">+50K</p>
                <p className="text-sm text-white/70">طلب</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-2xl font-bold">+200</p>
                <p className="text-sm text-white/70">مقهى</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-2xl font-bold">4.9</p>
                <p className="text-sm text-white/70">تقييم</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
